/**
 * The requirements views' UI model: filters in the URL, the hierarchy tree with roll-up
 * progress, table sorting and selection, the document outline, defect groups, quality
 * findings, what an item may do while a mission works it, and the review of AI proposals.
 * Pure, so it is unit tested.
 */
import {
  AI_JOB_STAGE_LABEL,
  BACKLOG_VIEWS,
  ITEM_KINDS,
  ITEM_KIND_LABEL,
  ITEM_PRIORITIES,
  ITEM_STATUSES,
  ITEM_WORKFLOW,
  MISSION_KINDS,
  PARENT_KINDS,
  SEVERITIES,
  byRank,
  isJobLive,
  itemProgress,
  jobProgress,
  keyNumber,
  qualityIssues,
} from '@orgus/protocol';
import type {
  AiJob,
  AiJobsState,
  BacklogItem,
  BacklogViewKind,
  Id,
  ItemKind,
  ItemLink,
  ItemPriority,
  ItemStatus,
  Proposal,
  ProposedItem,
  QualityIssue,
  SavedView,
  Severity,
  ViewFilter,
} from '@orgus/protocol';
import { formatDuration, formatMoney } from '@orgus/ui';
import type { MissionRow } from '../missions/model.ts';

export const AIJOBS_EXT = 'aijobs';

// ---------------------------------------------------------------------------
// Filters (in the URL)
// ---------------------------------------------------------------------------

export interface ReqFilter extends ViewFilter {
  projectId?: Id | null;
}

const list = <T extends string | number>(raw: string | null, allowed: readonly T[], num = false): T[] | undefined => {
  if (!raw) return undefined;
  const out = raw
    .split(',')
    .map((x) => (num ? (Number(x) as T) : (x as T)))
    .filter((x) => allowed.includes(x));
  return out.length ? out : undefined;
};

/** The filter a URL holds: ?kind=story,defect&status=ready&prio=0,1&sev=critical&label=x&owner=&q=&issues=1&project=. */
export function readFilter(p: URLSearchParams): ReqFilter {
  const f: ReqFilter = {};
  const kinds = list(p.get('kind')?.replace(/\bbug\b/g, 'defect') ?? null, ITEM_KINDS);
  if (kinds) f.kinds = kinds;
  const statuses = list(p.get('status'), ITEM_STATUSES);
  if (statuses) f.statuses = statuses;
  const prios = list(p.get('prio'), ITEM_PRIORITIES, true);
  if (prios) f.priorities = prios;
  const sev = list(p.get('sev'), SEVERITIES);
  if (sev) f.severities = sev;
  const labels = p.get('label')?.split(',').map((x) => x.trim().toLowerCase()).filter(Boolean);
  if (labels?.length) f.labels = labels;
  if (p.has('owner')) f.owner = p.get('owner') || null;
  const q = p.get('q');
  if (q) f.q = q;
  if (p.get('issues') === '1') f.issues = true;
  const project = p.get('project');
  if (project) f.projectId = project;
  return f;
}

/** Write a filter into URL params (keeping everything else). */
export function writeFilter(p: URLSearchParams, f: ReqFilter): URLSearchParams {
  const n = new URLSearchParams(p);
  const set = (k: string, v: string | null | undefined) => (v ? n.set(k, v) : n.delete(k));
  set('kind', f.kinds?.join(','));
  set('status', f.statuses?.join(','));
  set('prio', f.priorities?.join(','));
  set('sev', f.severities?.join(','));
  set('label', f.labels?.join(','));
  if (f.owner !== undefined) n.set('owner', f.owner ?? '');
  else n.delete('owner');
  set('q', f.q);
  set('issues', f.issues ? '1' : null);
  set('project', f.projectId ?? null);
  return n;
}

/** How many filters (besides the project) narrow the list. */
export function filterCount(f: ReqFilter): number {
  return (f.kinds?.length ? 1 : 0) + (f.statuses?.length ? 1 : 0) + (f.priorities?.length ? 1 : 0) + (f.severities?.length ? 1 : 0) + (f.labels?.length ? 1 : 0) + (f.owner !== undefined ? 1 : 0) + (f.q?.trim() ? 1 : 0) + (f.issues ? 1 : 0);
}

export function matchesFilter(i: BacklogItem, f: ReqFilter, issues?: ReadonlyMap<Id, QualityIssue[]>): boolean {
  if (f.projectId && i.projectId !== f.projectId) return false;
  if (f.kinds?.length && !f.kinds.includes(i.kind)) return false;
  if (f.statuses?.length ? !f.statuses.includes(i.status) : i.status === 'dropped') return false;
  if (f.priorities?.length && !f.priorities.includes(i.priority)) return false;
  if (f.severities?.length && (!i.defect || !f.severities.includes(i.defect.severity))) return false;
  if (f.labels?.length && !f.labels.every((l) => i.labels.includes(l))) return false;
  if (f.owner !== undefined && (i.owner ?? null) !== (f.owner || null)) return false;
  if (f.issues && !(issues?.get(i.id)?.length ?? 0)) return false;
  const q = f.q?.trim().toLowerCase();
  if (q) {
    const hay = `${i.key} ${i.title} ${i.body} ${i.owner ?? ''} ${i.labels.join(' ')} ${i.components.join(' ')} ${ITEM_KIND_LABEL[i.kind]} ${i.acceptance.map((c) => c.text).join(' ')}`.toLowerCase();
    if (!q.split(/\s+/).every((w) => hay.includes(w))) return false;
  }
  return true;
}

export function filterReq(items: readonly BacklogItem[], f: ReqFilter, issues?: ReadonlyMap<Id, QualityIssue[]>): BacklogItem[] {
  return items.filter((i) => matchesFilter(i, f, issues));
}

/** A saved view as URL params for the page. */
export function viewParams(v: SavedView, base: URLSearchParams): URLSearchParams {
  const p = writeFilter(new URLSearchParams(), { ...v.filter, ...(v.projectId ? { projectId: v.projectId } : {}) });
  for (const k of ['tab', 'item']) if (base.get(k)) p.set(k, base.get(k)!);
  p.set('view', v.view);
  p.set('saved', v.id);
  if (v.sort) p.set('sort', `${v.sort.key}:${v.sort.dir}`);
  return p;
}

export function readView(p: URLSearchParams): BacklogViewKind {
  const v = p.get('view');
  if (v === 'list') return 'table';
  return BACKLOG_VIEWS.includes(v as BacklogViewKind) ? (v as BacklogViewKind) : 'tree';
}

/** Every label in use, most used first. */
export function labelsInUse(items: readonly BacklogItem[]): string[] {
  const n = new Map<string, number>();
  for (const i of items) for (const l of i.labels) n.set(l, (n.get(l) ?? 0) + 1);
  return [...n.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).map(([l]) => l);
}

export function ownersInUse(items: readonly BacklogItem[]): string[] {
  return [...new Set(items.map((i) => i.owner).filter((o): o is string => !!o))].sort();
}

// ---------------------------------------------------------------------------
// Quality findings
// ---------------------------------------------------------------------------

export function qualityIndex(items: readonly BacklogItem[], links: readonly ItemLink[]): Map<Id, QualityIssue[]> {
  const out = new Map<Id, QualityIssue[]>();
  const byProject = new Map<Id, BacklogItem[]>();
  for (const i of items) {
    const list = byProject.get(i.projectId);
    if (list) list.push(i);
    else byProject.set(i.projectId, [i]);
  }
  for (const i of items) {
    const q = qualityIssues(i, { items: byProject.get(i.projectId)!, links });
    if (q.length) out.set(i.id, q);
  }
  return out;
}

/** Only the findings worth a mark in a list: warnings first. */
export function headlineIssue(issues: readonly QualityIssue[] | undefined): QualityIssue | undefined {
  if (!issues?.length) return undefined;
  return issues.find((i) => i.level === 'warn') ?? issues[0];
}

// ---------------------------------------------------------------------------
// Tree
// ---------------------------------------------------------------------------

export interface TreeNode {
  item: BacklogItem;
  depth: number;
  children: TreeNode[];
  /** Roll-up over everything below. */
  progress: ReturnType<typeof itemProgress>;
  /** False for an ancestor shown only because something below it matched. */
  matched: boolean;
}

/**
 * The hierarchy of the items that match, with every ancestor of a match kept (shown dimmed)
 * so nothing loses its place. Roots are items without a parent in view, in rank order.
 */
export function buildTree(items: readonly BacklogItem[], f: ReqFilter, issues?: ReadonlyMap<Id, QualityIssue[]>): TreeNode[] {
  const byId = new Map(items.map((i) => [i.id, i]));
  const matched = new Set(filterReq(items, f, issues).map((i) => i.id));
  const keep = new Set(matched);
  for (const id of matched) {
    let p = byId.get(id)?.parentId;
    for (let hops = 0; p && hops < 50; hops++) {
      if (keep.has(p)) break;
      keep.add(p);
      p = byId.get(p)?.parentId;
    }
  }
  const kids = new Map<Id | null, BacklogItem[]>();
  for (const i of items) {
    if (!keep.has(i.id)) continue;
    const parent = i.parentId && keep.has(i.parentId) ? i.parentId : null;
    const arr = kids.get(parent) ?? [];
    arr.push(i);
    kids.set(parent, arr);
  }
  const make = (i: BacklogItem, depth: number, seen: Set<Id>): TreeNode => {
    seen.add(i.id);
    const children = (kids.get(i.id) ?? []).filter((c) => !seen.has(c.id)).sort(treeOrder).map((c) => make(c, depth + 1, seen));
    return { item: i, depth, children, progress: itemProgress(i, items), matched: matched.has(i.id) };
  };
  const seen = new Set<Id>();
  return (kids.get(null) ?? []).sort(treeOrder).map((i) => make(i, 0, seen));
}

const KIND_ORDER: Record<ItemKind, number> = { epic: 0, feature: 1, story: 2, spike: 3, task: 4, defect: 5, chore: 6 };

/** Siblings: epics before loose items, then rank. */
export function treeOrder(a: BacklogItem, b: BacklogItem): number {
  if (!a.parentId && !b.parentId && KIND_ORDER[a.kind] !== KIND_ORDER[b.kind]) return KIND_ORDER[a.kind] - KIND_ORDER[b.kind];
  return byRank(a, b);
}

export interface TreeRow {
  node: TreeNode;
  expanded: boolean;
  hasChildren: boolean;
}

/** The rows on screen: expanded nodes show their children. */
export function visibleRows(roots: readonly TreeNode[], expanded: ReadonlySet<Id>): TreeRow[] {
  const out: TreeRow[] = [];
  const walk = (n: TreeNode) => {
    const open = expanded.has(n.item.id);
    out.push({ node: n, expanded: open, hasChildren: n.children.length > 0 });
    if (open) for (const c of n.children) walk(c);
  };
  for (const r of roots) walk(r);
  return out;
}

/** Epics and features start open; so does everything when a filter narrows the tree. */
export function defaultExpanded(roots: readonly TreeNode[], all = false): Set<Id> {
  const out = new Set<Id>();
  const walk = (n: TreeNode) => {
    if (n.children.length && (all || n.item.kind === 'epic' || n.item.kind === 'feature')) out.add(n.item.id);
    n.children.forEach(walk);
  };
  roots.forEach(walk);
  return out;
}

/** Arrow keys in the tree: up and down move, right opens or goes to the first child, left closes or goes to the parent. */
export function treeKey(rows: readonly TreeRow[], activeId: Id | null, key: string): { active?: Id; toggle?: Id } {
  const i = Math.max(0, rows.findIndex((r) => r.node.item.id === activeId));
  const row = rows[i];
  if (!row) return {};
  switch (key) {
    case 'ArrowDown':
    case 'j':
      return { active: rows[Math.min(rows.length - 1, activeId ? i + 1 : 0)]!.node.item.id };
    case 'ArrowUp':
    case 'k':
      return { active: rows[Math.max(0, i - 1)]!.node.item.id };
    case 'ArrowRight':
      if (row.hasChildren && !row.expanded) return { toggle: row.node.item.id };
      if (row.hasChildren) return { active: row.node.children[0]!.item.id };
      return {};
    case 'ArrowLeft': {
      if (row.hasChildren && row.expanded) return { toggle: row.node.item.id };
      for (let k = i - 1; k >= 0; k--) if (rows[k]!.node.depth < row.node.depth) return { active: rows[k]!.node.item.id };
      return {};
    }
    case 'Home':
      return { active: rows[0]!.node.item.id };
    case 'End':
      return { active: rows[rows.length - 1]!.node.item.id };
    default:
      return {};
  }
}

// ---------------------------------------------------------------------------
// Table
// ---------------------------------------------------------------------------

export type SortKey = 'key' | 'title' | 'kind' | 'status' | 'priority' | 'severity' | 'estimate' | 'owner' | 'updated';
export const SORT_KEYS: readonly SortKey[] = ['key', 'title', 'kind', 'status', 'priority', 'severity', 'estimate', 'owner', 'updated'];
export interface Sort {
  key: SortKey;
  dir: 'asc' | 'desc';
}

export function readSort(raw: string | null): Sort {
  const [k, d] = (raw ?? '').split(':');
  return SORT_KEYS.includes(k as SortKey) ? { key: k as SortKey, dir: d === 'desc' ? 'desc' : 'asc' } : { key: 'priority', dir: 'asc' };
}

/** QAWL-2 before QAWL-10. */
export function compareKeys(a: string, b: string): number {
  const pa = a.replace(/-\d+$/, '');
  const pb = b.replace(/-\d+$/, '');
  return pa.localeCompare(pb) || (keyNumber(a) ?? 0) - (keyNumber(b) ?? 0);
}

const SEV_RANK: Record<Severity, number> = { critical: 0, major: 1, minor: 2, trivial: 3 };

export function sortItems(items: readonly BacklogItem[], s: Sort): BacklogItem[] {
  const val = (i: BacklogItem): string | number | null => {
    switch (s.key) {
      case 'key':
        return null;
      case 'title':
        return i.title.toLowerCase();
      case 'kind':
        return KIND_ORDER[i.kind];
      case 'status':
        return ITEM_STATUSES.indexOf(i.status);
      case 'priority':
        return i.priority;
      case 'severity':
        return i.defect ? SEV_RANK[i.defect.severity] : null;
      case 'estimate':
        return i.estimate;
      case 'owner':
        return i.owner?.toLowerCase() ?? null;
      case 'updated':
        return i.updatedAt;
    }
  };
  const dir = s.dir === 'asc' ? 1 : -1;
  return [...items].sort((a, b) => {
    if (s.key === 'key') return dir * compareKeys(a.key, b.key);
    const x = val(a);
    const y = val(b);
    // Blanks last whichever way it sorts.
    if (x == null && y == null) return compareKeys(a.key, b.key);
    if (x == null) return 1;
    if (y == null) return -1;
    const c = x < y ? -1 : x > y ? 1 : 0;
    return dir * c || b.updatedAt - a.updatedAt || compareKeys(a.key, b.key);
  });
}

/** Selection with Shift: everything between the anchor and this row. */
export function selectRange(order: readonly Id[], current: ReadonlySet<Id>, anchor: Id | null, id: Id, extend: boolean): Set<Id> {
  const next = new Set(current);
  if (extend && anchor && order.includes(anchor)) {
    const a = order.indexOf(anchor);
    const b = order.indexOf(id);
    for (let k = Math.min(a, b); k <= Math.max(a, b); k++) next.add(order[k]!);
    return next;
  }
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
}

/** What every selected item shares, for the bulk bar: a value, or 'mixed'. */
export function common<T>(items: readonly BacklogItem[], pick: (i: BacklogItem) => T): T | 'mixed' | undefined {
  if (!items.length) return undefined;
  const first = pick(items[0]!);
  return items.every((i) => pick(i) === first) ? first : 'mixed';
}

/** Statuses every selected item's workflow allows. */
export function sharedStatuses(items: readonly BacklogItem[]): ItemStatus[] {
  return ITEM_STATUSES.filter((s) => items.every((i) => workflowOf(i.kind).includes(s)));
}

export function workflowOf(kind: ItemKind): readonly ItemStatus[] {
  return ITEM_WORKFLOW[kind];
}

// ---------------------------------------------------------------------------
// Document view
// ---------------------------------------------------------------------------

export interface DocSection {
  item: BacklogItem;
  depth: number;
  /** "1", "1.2", "1.2.3". */
  number: string;
}

/** An epic (or feature, or story) and everything under it, numbered like a spec, dropped items left out. */
export function docOutline(rootId: Id, items: readonly BacklogItem[]): DocSection[] {
  const out: DocSection[] = [];
  const walk = (pid: Id, depth: number, prefix: string) => {
    const kids = items.filter((i) => i.parentId === pid && i.status !== 'dropped').sort(byRank);
    kids.forEach((c, n) => {
      const number = prefix ? `${prefix}.${n + 1}` : String(n + 1);
      out.push({ item: c, depth, number });
      if (depth < 6) walk(c.id, depth + 1, number);
    });
  };
  walk(rootId, 0, '');
  return out;
}

/** Items that can head a document: epics and features with something under them, epics first. */
export function docRoots(items: readonly BacklogItem[], projectId?: Id | null): BacklogItem[] {
  const parents = new Set(items.map((i) => i.parentId).filter(Boolean));
  return items.filter((i) => (!projectId || i.projectId === projectId) && i.status !== 'dropped' && (i.kind === 'epic' || i.kind === 'feature') && parents.has(i.id)).sort((a, b) => KIND_ORDER[a.kind] - KIND_ORDER[b.kind] || byRank(a, b));
}

/** The document as Markdown (for copying into a pull request or a wiki). */
export function docMarkdown(root: BacklogItem, items: readonly BacklogItem[]): string {
  const lines = [`# ${root.key} ${root.title}`, ''];
  if (root.body.trim()) lines.push(root.body.trim(), '');
  for (const s of docOutline(root.id, items)) {
    lines.push(`${'#'.repeat(Math.min(6, s.depth + 2))} ${s.number} ${s.item.title} (${ITEM_KIND_LABEL[s.item.kind]} ${s.item.key})`, '');
    if (s.item.body.trim()) lines.push(s.item.body.trim(), '');
    if (s.item.acceptance.length) {
      lines.push('Acceptance criteria:', '');
      for (const c of s.item.acceptance) lines.push(`- [${c.done ? 'x' : ' '}] ${c.text}`);
      lines.push('');
    }
  }
  return lines.join('\n').trim();
}

// ---------------------------------------------------------------------------
// Defects
// ---------------------------------------------------------------------------

export interface DefectGroup {
  severity: Severity;
  items: BacklogItem[];
}

/** Open defects by severity (critical first), each group by priority then age. */
export function defectGroups(items: readonly BacklogItem[], showClosed = false): DefectGroup[] {
  const defects = items.filter((i) => i.kind === 'defect' && (showClosed || (i.status !== 'done' && i.status !== 'dropped')));
  return SEVERITIES.map((severity) => ({
    severity,
    items: defects.filter((d) => (d.defect?.severity ?? 'major') === severity).sort((a, b) => a.priority - b.priority || a.createdAt - b.createdAt),
  })).filter((g) => g.items.length);
}

/** What a defect affects, by key and title. */
export function affectedBy(defectId: Id, links: readonly ItemLink[], items: ReadonlyMap<Id, BacklogItem>): BacklogItem[] {
  return links.filter((l) => l.type === 'affects' && l.from === defectId).map((l) => items.get(l.to)).filter((x): x is BacklogItem => !!x);
}

// ---------------------------------------------------------------------------
// Items and their missions
// ---------------------------------------------------------------------------

/** The mission working an item right now (running or waiting), newest first. */
export function liveMission(rows: readonly MissionRow[], itemId: Id): MissionRow | null {
  return rows.filter((r) => r.mission.itemId === itemId && (r.view.state === 'running' || r.view.state === 'waiting')).sort((a, b) => b.mission.createdAt - a.mission.createdAt)[0] ?? null;
}

export type StartAbility =
  | { kind: 'mission' }
  | { kind: 'retry'; row: MissionRow }
  | { kind: 'sequence' }
  | { kind: 'running'; row: MissionRow }
  | { kind: 'none'; reason: string };

/**
 * What the item's primary action is: open the mission that works it, retry the last one when it
 * failed or stopped, start a mission, run its children as a sequence (epics and features never
 * start a mission of their own), or nothing.
 */
export function startAbility(item: BacklogItem, rows: readonly MissionRow[], items: readonly BacklogItem[]): StartAbility {
  const live = liveMission(rows, item.id);
  if (live) return { kind: 'running', row: live };
  if (item.status === 'done' || item.status === 'dropped') return { kind: 'none', reason: `It is ${item.status === 'done' ? 'done' : 'dropped'}.` };
  if (MISSION_KINDS.includes(item.kind)) {
    const last = rows.filter((r) => r.mission.itemId === item.id).sort((a, b) => b.mission.createdAt - a.mission.createdAt)[0];
    if (last && (last.view.state === 'failed' || last.view.state === 'stopped')) return { kind: 'retry', row: last };
    return { kind: 'mission' };
  }
  const hasWork = items.some((i) => i.parentId === item.id && i.status !== 'done' && i.status !== 'dropped');
  return hasWork ? { kind: 'sequence' } : { kind: 'none', reason: `${ITEM_KIND_LABEL[item.kind]}s run as a sequence of their stories, and this one has none open.` };
}

/** Parents an item may move under: same project, a kind that can hold it, not itself or below it. */
export function parentOptions(items: readonly BacklogItem[], self: Pick<BacklogItem, 'id' | 'kind' | 'projectId'> | { id?: undefined; kind: ItemKind; projectId: Id }): BacklogItem[] {
  const below = new Set<Id>();
  if (self.id) {
    const walk = (pid: Id) => {
      for (const c of items) if (c.parentId === pid && !below.has(c.id)) (below.add(c.id), walk(c.id));
    };
    walk(self.id);
  }
  return items
    .filter((i) => i.projectId === self.projectId && i.id !== self.id && !below.has(i.id) && PARENT_KINDS[self.kind].includes(i.kind) && i.status !== 'dropped')
    .sort((a, b) => KIND_ORDER[a.kind] - KIND_ORDER[b.kind] || compareKeys(a.key, b.key));
}

/** Items a link can point at: same project, not itself, by key. */
export function linkTargets(items: readonly BacklogItem[], self: Pick<BacklogItem, 'id' | 'projectId'>, q: string): BacklogItem[] {
  const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
  return items
    .filter((i) => i.projectId === self.projectId && i.id !== self.id && i.status !== 'dropped')
    .filter((i) => !words.length || words.every((w) => `${i.key} ${i.title}`.toLowerCase().includes(w)))
    .sort((a, b) => compareKeys(a.key, b.key))
    .slice(0, 50);
}

// ---------------------------------------------------------------------------
// Reviewing proposals
// ---------------------------------------------------------------------------

export interface ReviewNode {
  item: ProposedItem;
  depth: number;
  children: ReviewNode[];
}

export function proposalTree(items: readonly ProposedItem[]): ReviewNode[] {
  const refs = new Set(items.map((i) => i.ref));
  const make = (i: ProposedItem, depth: number, seen: Set<string>): ReviewNode => {
    seen.add(i.ref);
    return { item: i, depth, children: items.filter((c) => c.parentRef === i.ref && !seen.has(c.ref)).map((c) => make(c, depth + 1, seen)) };
  };
  const seen = new Set<string>();
  return items.filter((i) => !i.parentRef || !refs.has(i.parentRef)).map((i) => make(i, 0, seen));
}

/** Flattened in tree order, for lists and keyboard. */
export function flatReview(nodes: readonly ReviewNode[]): ReviewNode[] {
  const out: ReviewNode[] = [];
  const walk = (n: ReviewNode) => {
    out.push(n);
    n.children.forEach(walk);
  };
  nodes.forEach(walk);
  return out;
}

/**
 * Accept or reject one proposed item. Accepting a child accepts the ancestors it needs;
 * rejecting a parent leaves its children as they are (they move up when applied).
 */
export function decide(items: readonly ProposedItem[], ref: string, decision: ProposedItem['decision'], withChildren = false): ProposedItem[] {
  const byRef = new Map(items.map((i) => [i.ref, i]));
  const touched = new Set([ref]);
  if (decision === 'accepted') {
    for (let r = byRef.get(ref)?.parentRef, hops = 0; r && hops < 50; hops++) {
      const p = byRef.get(r);
      if (!p || p.decision === 'accepted') break;
      if (p.decision === 'pending') touched.add(p.ref);
      r = p.parentRef;
    }
  }
  if (withChildren) {
    const walk = (r: string) => {
      for (const c of items) if (c.parentRef === r && !touched.has(c.ref)) (touched.add(c.ref), walk(c.ref));
    };
    walk(ref);
  }
  return items.map((i) => (touched.has(i.ref) ? { ...i, decision: i.ref === ref || withChildren || decision === 'accepted' ? decision : i.decision } : i));
}

export function decideAll(items: readonly ProposedItem[], decision: ProposedItem['decision']): ProposedItem[] {
  return items.map((i) => ({ ...i, decision }));
}

export function decisionCounts(p: Pick<Proposal, 'items' | 'edits' | 'links'>): { accepted: number; rejected: number; pending: number; total: number } {
  const all = [...p.items, ...p.edits, ...p.links];
  const c = { accepted: 0, rejected: 0, pending: 0, total: all.length };
  for (const x of all) c[x.decision]++;
  return c;
}

/** Proposed items that trace back to a section (or a sub-section of it). */
export function itemsForSection(items: readonly ProposedItem[], sectionId: string): ProposedItem[] {
  return items.filter((i) => i.sections.some((s) => s === sectionId || s.startsWith(`${sectionId}.`)));
}

// ---------------------------------------------------------------------------
// AI jobs
// ---------------------------------------------------------------------------

export function aiJobsSlice(ext: Record<string, unknown>): AiJobsState | null {
  const s = ext[AIJOBS_EXT] as AiJobsState | undefined;
  return s && Array.isArray(s.jobs) && s.settings ? s : null;
}

export function liveJobs(s: AiJobsState | null): AiJob[] {
  return (s?.jobs ?? []).filter((j) => isJobLive(j.state));
}

/** "Writing, 45 s, $0.04" for a running job; "Done in 1 min 12 s, $0.12" when finished. */
export function jobLine(j: AiJob, now: number): string {
  const cost = j.cost != null ? `, ${formatMoney(j.cost)}` : '';
  if (isJobLive(j.state)) return `${AI_JOB_STAGE_LABEL[j.stage]}, ${formatDuration(Math.max(0, now - (j.startedAt ?? j.createdAt)))}${cost}`;
  const took = j.endedAt && j.startedAt ? ` in ${formatDuration(j.endedAt - j.startedAt)}` : '';
  if (j.state === 'done') return `Done${took}${cost}`;
  if (j.state === 'cancelled') return `Cancelled${took}`;
  return `Failed${took}${cost}`;
}

export { jobProgress };

/** Priority words for the AI's proposals and the table. */
export const PRIORITY_SHORT: Record<ItemPriority, string> = { 0: 'Urgent', 1: 'High', 2: 'Medium', 3: 'Low' };

/**
 * A column whose value is the same on every visible row says nothing: hide it and say the value
 * once in a header (spacing-layout.md, Rows). Only from `min` rows, so a short list keeps it.
 */
/**
 * Whether an owner column says anything: only when the rows have two or more different owners.
 * One shared owner (with or without unassigned rows) repeats the same face on every row, so it
 * shows on none and the owner reads on hover instead.
 */
export function ownersDiffer(owners: readonly (string | null | undefined)[]): boolean {
  return new Set(owners.filter((o): o is string => !!o)).size > 1;
}

/** "Owner: srikanth" for a row's hover when the owner column is hidden. */
export function ownerHint(owner: string | null | undefined): string | undefined {
  return owner ? `Owner: ${owner}` : undefined;
}

export function isConstantColumn<T>(values: readonly T[], min = 5): boolean {
  if (values.length < min) return false;
  const first = values[0];
  return values.every((v) => v === first);
}
